import React, { useState } from 'react';
import { Search, RefreshCw, Download, Eye, User } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { useNavigate } from 'react-router-dom';
import MainService from '../services/mainservice';
import * as XLSX from 'xlsx';

const TrackByBillNo = () => {
  const [voucherNo, setVoucherNo] = useState('');
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { showSuccess, showError, showWarning } = useToast();
  const navigate = useNavigate();

  const handleSearch = async () => {
    const voucherNumbers = voucherNo.split('.').filter(v => v.trim() !== '');

    if (voucherNumbers.length === 0) {
      showWarning('Please enter valid voucher numbers');
      return;
    }

    try {
      setIsLoading(true);

      const tokenData = localStorage.getItem('AINSOFT_DRIVER_TOKEN');
      if (!tokenData) {
        showError('Please login first');
        return;
      }

      const userData = JSON.parse(tokenData);
      const data = {
        VoucherNos: voucherNumbers,
        ClientId: userData.ClientId
      };

      const response = await MainService.TrackOrdersByBillNosPortal(data, userData.Token);

      if (response &&  response.length > 0) {
        setOrders(response);
        showSuccess(`Found ${response.length} order(s) for bill number "${voucherNo}"`);
      } else {
        setOrders([]);
        showWarning(`No orders found for bill number "${voucherNo}"`);
      }
    } catch (error) {
      console.error('Error searching orders:', error);
      showError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Excel export function
  const exportToExcel = () => {
    if (orders.length === 0) {
      showWarning('No orders to export');
      return;
    }

    try {
      const exportData = orders.map((order, index) => ({
        '#': index + 1,
        'Bill Number': order.ClientBillNo || 'N/A',
        'Voucher No': order.VoucherNo || 'N/A',
        'Customer Name': order.Customer?.Name || 'N/A',
        'Mobile No1': order.Customer?.MobileNo1 || 'N/A',
        'Mobile No2': order.Customer?.MobileNo2 || 'N/A',
        'Status': order.Status?.Name || 'N/A',
        'Address': order.CustomerAddress?.Address || 'N/A',
        'Location': order.CustomerAddress?.Location?.Name || 'N/A',
        'Emirate': order.CustomerAddress?.Emirate?.Name || 'N/A',
        'Order Date': order.Date ? new Date(order.Date).toLocaleDateString('en-GB') : 'N/A',
        'Pickup Date': order.PickupDate ? new Date(order.PickupDate).toLocaleDateString('en-GB') : 'N/A',
        'Delivery Date': order.DeliveryDate ? new Date(order.DeliveryDate).toLocaleDateString('en-GB') : 'N/A',
        'Product Price': order.ProductPrice ? `AED ${parseFloat(order.ProductPrice).toFixed(2)}` : 'N/A',
        'Delivery Charge': order.DeliveryCharge ? `AED ${parseFloat(order.DeliveryCharge).toFixed(2)}` : 'N/A',
        'Tax': order.TAX ? `AED ${parseFloat(order.TAX).toFixed(2)}` : 'N/A',
        'Collection Amount': order.CustomerAmount ? `AED ${parseFloat(order.CustomerAmount).toFixed(2)}` : 'N/A',
        'My Payment': order.ClientAmount ? `AED ${parseFloat(order.ClientAmount).toFixed(2)}` : 'N/A'
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Search Results');

      const fileName = `bill_search_results_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

      showSuccess('Excel file exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      showError('Failed to export to Excel');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return `AED ${parseFloat(amount).toFixed(2)}`;
  };

  const getStatusStyle = (color) => {
    if (!color) return {};

    // Parse color if it's a string like "rgb(255,0,0)" or "#ff0000"
    let backgroundColor = color;
    let textColor = '#ffffff';

    if (color.startsWith('rgb')) {
      backgroundColor = color;
    } else if (color.startsWith('#')) {
      backgroundColor = color;
    } else {
      // If it's a color name, use it directly
      backgroundColor = color;
    }

    return {
      backgroundColor,
      color: textColor,
      border: `1px solid ${backgroundColor}`
    };
  };

  if (isLoading) {
    return (
      <div className="page-container flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-primary-600">Searching orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Track by Bill Number</h1>
          <p className="text-gray-600">Search orders by client bill number</p>
        </div>

        {/* Search Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bill Number Search
              </label>
              <input
                type="text"
                value={voucherNo}
                onChange={(e) => setVoucherNo(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter bill number"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-end gap-3">
              <button
                onClick={handleSearch}
                className="bg-green-500 hover:bg-green-600 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 flex items-center space-x-2"
              >
                <Search className="w-4 h-4" />
                <span>Search</span>
              </button>
              <button
                onClick={() => {
                  setVoucherNo('');
                  setOrders([]);
                }}
                className="bg-gray-500 hover:bg-gray-600 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 flex items-center space-x-2"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Clear</span>
              </button>
            </div>
          </div>
        </div>

        {/* Results Section */}
        {orders.length > 0 ? (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  Search Results ({orders.length} orders found)
                </h2>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={exportToExcel}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-200"
                  >
                    <Download className="w-4 h-4" />
                    <span>Export Excel</span>
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-100 border-b border-gray-200">
                      <th className="text-center py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">#</th>
                      <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">BILL NUMBER</th>
                      <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">VOUCHER NO</th>
                      <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">CUSTOMER</th>
                      <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">STATUS</th>
                      <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">ADDRESS</th>
                      <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">LOCATION</th>
                      <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">EMIRATE</th>
                      <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">ORDER DATE</th>
                      <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">DELIVERY DATE</th>
                      <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">PRODUCT PRICE</th>
                      <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">DELIVERY CHARGE</th>
                      <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">TAX</th>
                      <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">COLLECTION AMOUNT</th>
                      <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">MY PAYMENT</th>
                      <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                     {orders.map((order, index) => (
                       <tr
                         key={index}
                         className="border-b border-gray-200 hover:bg-gray-50 transition-colors duration-200"
                       >
                          <td className="py-4 px-4 text-sm text-center text-gray-500 font-medium">
                            {index + 1}
                          </td>
                          <td className="py-4 px-4 text-sm font-medium text-gray-900">
                            <div className="flex items-center">
                              <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                              {order.ClientBillNo || 'N/A'}
                            </div>
                          </td>
                          <td className="py-4 px-4 text-sm text-gray-700">{order.VoucherNo || 'N/A'}</td>
                          <td className="py-4 px-4">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{order.Customer?.Name || 'N/A'}</div>
                              <div className="text-xs text-gray-500">{order.Customer?.MobileNo1 || 'N/A'}</div>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <span
                              className="inline-flex px-2 py-1 text-xs font-medium rounded-full"
                              style={getStatusStyle(order.Status?.Color)}
                            >
                              {order.Status?.Name || 'N/A'}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-sm text-gray-700 max-w-xs truncate">
                            {order.CustomerAddress?.Address || 'N/A'}
                          </td>
                          <td className="py-4 px-4 text-sm text-gray-700">
                            {order.CustomerAddress?.Location?.Name || 'N/A'}
                          </td>
                          <td className="py-4 px-4 text-sm text-gray-700">
                            {order.CustomerAddress?.Emirate?.Name || 'N/A'}
                          </td>
                          <td className="py-4 px-4 text-sm text-gray-700">
                            {formatDate(order.Date)}
                          </td>
                          <td className="py-4 px-4 text-sm text-gray-700">
                            {formatDate(order.DeliveryDate)}
                          </td>
                          <td className="py-4 px-4 text-sm font-semibold text-gray-900">
                            {formatCurrency(order.ProductPrice)}
                          </td>
                          <td className="py-4 px-4 text-sm text-gray-700">
                            {formatCurrency(order.DeliveryCharge)}
                          </td>
                          <td className="py-4 px-4 text-sm text-gray-700">
                            {formatCurrency(order.TAX)}
                          </td>
                          <td className="py-4 px-4 text-sm text-gray-700">
                            {formatCurrency(order.CustomerAmount)}
                          </td>
                          <td className="py-4 px-4 text-sm font-bold text-green-600">
                            {formatCurrency(order.ClientAmount)}
                          </td>
                          <td className="py-4 px-4">
                            <button
                              onClick={() => navigate(`/order-details/${order.Id}`)}
                              className="p-1 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded transition-colors duration-200"
                              title="View Order Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </td>
                         </tr>
                       ))}
                  </tbody>
                </table>
              </div>

            </div>
          </div>
        ) : (
          /* Message Box when no orders */
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-8 h-8 text-blue-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Search by Bill Number</h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Enter bill number to find orders. You can search by partial bill numbers or complete bill identifiers.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <div className="bg-gray-50 rounded-lg p-4 text-left">
                  <h4 className="font-medium text-gray-900 mb-2">Search Examples:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Bill Number: "INV-2024-001"</li>
                    <li>• Invoice ID: "12345"</li>
                    <li>• Reference: "REF-ABC-123"</li>
                    <li>• Partial bill: "INV-2024"</li>
                  </ul>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-left">
                  <h4 className="font-medium text-gray-900 mb-2">What you'll see:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• All orders for the bill number</li>
                    <li>• Order status and dates</li>
                    <li>• Delivery addresses</li>
                    <li>• Payment information</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackByBillNo;